import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// Language definitions
// ---------------------------------------------------------------------------
interface LangOption {
  code: string;       // i18n language code
  flagCode: string;   // flag-icons country code (fi fi-XX)
  label: string;      // human-readable name
  short: string;      // 2-char abbreviation shown in collapsed state
}

const LANGUAGES: LangOption[] = [
  { code: 'pt-BR', flagCode: 'br', label: 'Português', short: 'PT' },
  { code: 'en',    flagCode: 'us', label: 'English',   short: 'EN' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface LangSelectProps {
  className?: string;
  /** Open the dropdown upward (e.g. when placed at the bottom of the sidebar). */
  up?: boolean;
}

// ---------------------------------------------------------------------------
// LangSelect — animated dropdown language selector with flag icons
// ---------------------------------------------------------------------------
export function LangSelect({ className, up = false }: LangSelectProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang =
    LANGUAGES.find((l) => i18n.language?.startsWith(l.code) || l.code === i18n.language) ??
    LANGUAGES[1]; // fallback to English

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = useCallback(
    (lang: LangOption) => {
      void i18n.changeLanguage(lang.code);
      setOpen(false);
    },
    [i18n]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((o) => !o);
    }
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger button — collapsed state: chevron + flag + short code */}
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${currentLang.label}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex items-center gap-1.5 rounded-lg px-2 py-1.5 transition-all duration-150',
          'text-foreground/70 hover:text-foreground hover:bg-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          'active:scale-95',
          open && 'bg-muted text-foreground'
        )}
      >
        {/* Chevron indicator */}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex items-center"
          aria-hidden="true"
        >
          <ChevronDown size={12} className="text-muted-foreground" />
        </motion.span>

        {/* Current language flag — the trigger shows ONLY the flag (+ chevron);
            the language name/short code live in the dropdown list. */}
        <span
          className={`fi fi-${currentLang.flagCode}`}
          style={{ fontSize: '15px', lineHeight: 1, borderRadius: '2px', flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown list — animated */}
      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            aria-label="Select language"
            initial={{ opacity: 0, scale: 0.95, y: up ? 6 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: up ? 6 : -6 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'absolute right-0 z-[200] min-w-[140px]',
              up
                ? 'bottom-full mb-1.5 origin-bottom-right'
                : 'top-full mt-1.5 origin-top-right',
              'rounded-xl border border-border bg-popover p-1 shadow-lg',
              'ring-1 ring-foreground/5'
            )}
          >
            {LANGUAGES.map((lang, idx) => {
              const isSelected = lang.code === currentLang.code;
              return (
                <motion.li
                  key={lang.code}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.12, delay: idx * 0.04 }}
                  role="option"
                  aria-selected={isSelected}
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(lang)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                      isSelected
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-foreground hover:bg-muted'
                    )}
                  >
                    {/* Flag */}
                    <span
                      className={`fi fi-${lang.flagCode}`}
                      style={{ fontSize: '16px', lineHeight: 1, borderRadius: '2px', flexShrink: 0 }}
                      aria-hidden="true"
                    />
                    {/* Language name */}
                    <span className="flex-1 text-left">{lang.label}</span>
                    {/* Short code badge */}
                    <span
                      className={cn(
                        'text-[10px] font-mono font-bold px-1.5 py-0.5 rounded',
                        isSelected
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {lang.short}
                    </span>
                  </button>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
